import { useState, useEffect } from 'react';
import { useComandosDoJogo } from '@/hooks/useComandosDoJogo';
import { Navigate } from 'react-router-dom';
import {
  Gift,
  Package,
  Wallet,
  Calendar,
  CheckCircle2,
  Loader2,
  AlertCircle,
  Sparkles,
  Clock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useStore } from '@/contexts/StoreContext';
import { ehLojaDigital } from '@/lib/loja-digital';
import { toast } from 'sonner';
import PageHero from '@/components/common/PageHero';
import { giftService, GiftRedemption } from '@/lib/services/gift.service';
import { useTextosDaLoja } from '@/construtor/textos';

import { Caixa } from '@/construtor/caixa';
export default function GiftsPage() {
  const { user, isAuthenticated, openLoginModal, updateCredits } = useAuth();
  const { config } = useTheme();
  const { store } = useStore();
  const { t } = useTextosDaLoja();
  const comandos = useComandosDoJogo();

  const [redemptions, setRedemptions] = useState<GiftRedemption[]>([]);
  const [loadingRedemptions, setLoadingRedemptions] = useState(false);
  // Link do presente do Discord: o código chega em `?codigo=` e sai da barra
  // de endereço na hora, para não ficar no histórico.
  const [giftCode, setGiftCode] = useState(() => {
    const codigo = new URLSearchParams(window.location.search).get('codigo')?.trim().toUpperCase() ?? '';
    if (codigo) window.history.replaceState(null, '', window.location.pathname);
    return /^[A-Z0-9-]{4,20}$/.test(codigo) ? codigo : '';
  });
  const [redeeming, setRedeeming] = useState(false);

  const fetchRedemptions = async () => {
    if (!store || !user?.id) return;

    setLoadingRedemptions(true);
    try {
      const data = await giftService.getPlayerRedemptions(store.id, user.id);
      setRedemptions(data);
    } catch (error: any) {
      console.error('Error fetching gift redemptions:', error);
      toast.error(t('store.gifts.load_error'));
    } finally {
      setLoadingRedemptions(false);
    }
  };

  const handleRedeemGift = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!giftCode.trim()) {
      toast.error(t('store.gifts.empty_code'));
      return;
    }

    if (!store || !user?.id) {
      toast.error(t('store.gifts.process_error'));
      return;
    }

    setRedeeming(true);
    try {
      const result = await giftService.redeemGift(store.id, user.id, {
        code: giftCode.trim().toUpperCase(),
      });

      // Success message based on gift type
      if (result.gift.type === 'CREDIT') {
        const creditAmount = result.gift.creditAmount || 0;
        toast.success(t('store.gifts.received_credits', { message: result.message, amount: formatCredits(creditAmount) }));
        
        // Update local credits balance
        if (updateCredits) {
          updateCredits(creditAmount);
        }
      } else {
        toast.success(t('store.gifts.received_product', { message: result.message }));
      }

      // Clear input and refresh list
      setGiftCode('');
      fetchRedemptions();
    } catch (error: any) {
      console.error('Error redeeming gift:', error);
      const errorMessage = error.response?.data?.message || t('store.gifts.redeem_error');
      toast.error(errorMessage);
    } finally {
      setRedeeming(false);
    }
  };

  const formatCredits = (credits: number) => {
    return new Intl.NumberFormat(config?.language || 'pt-BR', {
      style: 'currency',
      currency: config?.currency?.code || 'BRL',
    }).format(credits);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString(config?.language || 'pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getGiftTypeIcon = (type: string) => {
    switch (type) {
      case 'CREDIT':
        return <Wallet className="w-5 h-5 text-success" />;
      case 'PRODUCT':
        return <Package className="w-5 h-5 text-primary" />;
      default:
        return <Gift className="w-5 h-5 text-accent" />;
    }
  };

  const getGiftTypeLabel = (type: string) => {
    switch (type) {
      case 'CREDIT':
        return t('store.gifts.type_credit');
      case 'PRODUCT':
        return t('store.gifts.type_product');
      default:
        return type;
    }
  };

  // Fetch player gift redemptions
  useEffect(() => {
    if (store && isAuthenticated && user?.id) {
      fetchRedemptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, isAuthenticated, user?.id]);

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <div>
      <PageHero
        title={t('store.gifts.title')}
        breadcrumbs={[{ label: t('store.gifts.breadcrumb_profile'), href: '/perfil' }, { label: t('store.gifts.breadcrumb') }]}

      />

      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Redeem Gift Card */}
            <Caixa id="presentes.resgatar" miolo>
            <Card className="bg-card border-border">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="font-display">{t('store.gifts.redeem_title')}</CardTitle>
                    <CardDescription>{t('store.gifts.redeem_desc')}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Caixa id="presentes.resgatar.formulario">
                <form onSubmit={handleRedeemGift} className="space-y-4">
                  <div>
                    <Label htmlFor="giftCode">{t('store.gifts.code_label')}</Label>
                    <Input
                      id="giftCode"
                      type="text"
                      placeholder={t('store.gifts.code_placeholder')}
                      value={giftCode}
                      onChange={(e) => setGiftCode(e.target.value.toUpperCase())}
                      disabled={redeeming}
                      className="font-mono text-lg"
                      maxLength={20}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('store.gifts.code_hint')}
                    </p>
                    {!ehLojaDigital(store) && (
                      <p className="text-xs text-muted-foreground mt-1" data-testid="gifts-in-game-hint">
                        {t('store.gifts.in_game_hint', { resgatar: comandos.resgatar })}
                      </p>
                    )}
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-primary hover:bg-primary-hover text-primary-foreground"
                    disabled={redeeming || !giftCode.trim()}
                  >
                    {redeeming ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('store.gifts.redeeming')}
                      </>
                    ) : (
                      <>
                        <Gift className="w-4 h-4 mr-2" />
                        {t('store.gifts.redeem_button')}
                      </>
                    )}
                  </Button>
                </form>
                </Caixa>
              </CardContent>
            </Card>
            </Caixa>

            {/* Gift History */}
            <Caixa id="presentes.historico" miolo>
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="font-display">{t('store.gifts.history_title')}</CardTitle>
                <CardDescription>{t('store.gifts.history_desc')}</CardDescription>
              </CardHeader>
              <CardContent>
                {loadingRedemptions ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : redemptions.length === 0 ? (
                  <Caixa id="presentes.historico.vazio">
                  <div className="text-center py-12">
                    <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                      <Gift className="w-8 h-8 text-muted-foreground" />
                    </div>
                    <p className="text-muted-foreground mb-2">{t('store.gifts.empty_title')}</p>
                    <p className="text-sm text-muted-foreground">
                      {t('store.gifts.empty_desc')}
                    </p>
                  </div>
                  </Caixa>
                ) : (
                  <Caixa id="presentes.historico.lista">
                  <div className="space-y-4">
                    {redemptions.map((redemption) => (
                      <Caixa id="presentes.historico.item" repetida>
                      <div
                        key={redemption.id}
                        className="flex items-start gap-4 p-4 rounded-lg border border-border bg-card hover:bg-accent/5 transition-colors"
                      >
                        {/* Icon */}
                        <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                          {redemption.gift ? getGiftTypeIcon(redemption.gift.type) : <Gift className="w-5 h-5" />}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <h3 className="font-semibold text-foreground line-clamp-1">
                              {redemption.gift?.name || t('store.gifts.default_name')}
                            </h3>
                            <Badge variant="outline" className="flex-shrink-0">
                              {redemption.gift ? getGiftTypeLabel(redemption.gift.type) : t('store.gifts.default_name')}
                            </Badge>
                          </div>

                          <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Gift className="w-4 h-4" />
                              <span className="font-mono">{redemption.gift?.code}</span>
                            </div>
                            <Separator orientation="vertical" className="h-4" />
                            <div className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              <span>{formatDate(redemption.redeemedAt)}</span>
                            </div>
                          </div>

                          {/* Gift value */}
                          {redemption.gift?.type === 'CREDIT' && redemption.gift.creditAmount && (
                            <div className="mt-2">
                              <Badge variant="default" className="bg-success text-success-foreground">
                                <Wallet className="w-3 h-3 mr-1" />
                                {formatCredits(redemption.gift.creditAmount)}
                              </Badge>
                            </div>
                          )}
                        </div>

                        {/* Status */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <CheckCircle2 className="w-5 h-5 text-success" />
                        </div>
                      </div>
                      </Caixa>
                    ))}
                  </div>
                  </Caixa>
                )}
              </CardContent>
            </Card>
            </Caixa>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <Caixa id="presentes.como" miolo>
            <Card className="bg-card border-border sticky top-24">
              <CardHeader>
                <CardTitle className="font-display text-lg">{t('store.gifts.how_title')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Caixa id="presentes.como.passo1">
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-primary">1</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm mb-1">{t('store.gifts.step1_title')}</h4>
                    <p className="text-xs text-muted-foreground">{t('store.gifts.step1_desc')}</p>
                  </div>
                </div>
                </Caixa>

                <Separator />

                <Caixa id="presentes.como.passo2">
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-primary">2</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm mb-1">{t('store.gifts.step2_title')}</h4>
                    <p className="text-xs text-muted-foreground">{t('store.gifts.step2_desc')}</p>
                  </div>
                </div>
                </Caixa>

                <Separator />

                <Caixa id="presentes.como.passo3">
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-primary">3</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm mb-1">{t('store.gifts.step3_title')}</h4>
                    <p className="text-xs text-muted-foreground">{t('store.gifts.step3_desc')}</p>
                  </div>
                </div>
                </Caixa>

                <Separator />

                <Caixa id="presentes.como.atencao">
                <div className="p-3 rounded-lg bg-accent/10 border border-accent/30">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs text-accent font-medium mb-1">{t('store.gifts.warning_title')}</p>
                      <p className="text-xs text-muted-foreground">{t('store.gifts.warning_desc')}</p>
                    </div>
                  </div>
                </div>
                </Caixa>
              </CardContent>
            </Card>
            </Caixa>
          </div>
        </div>
      </div>
    </div>
  );
}
